import { useEffect, useRef, useState } from 'react';
import { springTo } from '@/lib/spring';

/**
 * The Voter data builder's motion helpers (the prototype's fluid-interface pass): a spring for counts, and when the
 * sentence has scrolled away. The delay that keeps quick answers from flashing a loading state is the
 * shared `useDelayedFlag`.
 */

/**
 * `target`, reached on a critically damped spring from whatever is on screen now. A new target
 * mid-flight keeps the current value and velocity, so the number never jumps. With reduced
 * motion it is simply `target`, and so is a change that lands while the tab is hidden: the
 * browser pauses animation frames there, and the number shouldn't be stale when the tab returns.
 */
export function useSpringNumber(target: number, reduced: boolean): number {
  const [shown, setShown] = useState(target);
  const spring = useRef({ value: target, velocity: 0 });

  useEffect(() => {
    const s = spring.current;
    if (reduced || document.hidden || (s.value === target && s.velocity === 0)) {
      spring.current = { value: target, velocity: 0 };
      setShown(target);
      return;
    }
    const animation = springTo({ from: s.value, to: target, velocity: s.velocity, set: setShown });
    return () => {
      spring.current = animation.stop();
    };
  }, [target, reduced]);

  return reduced ? target : shown;
}

/**
 * True once `element` has scrolled up out of view, so a floating copy can stand in for it. It takes
 * the element rather than a ref so it starts watching whenever the element mounts, not only on the
 * first render.
 */
export function useScrolledAway(element: Element | null): boolean {
  const [away, setAway] = useState(false);
  useEffect(() => {
    if (!element || typeof IntersectionObserver === 'undefined') return;
    // The app scrolls an inner container under its header, so "away" is out of view and above the
    // middle of the screen, not above the viewport's top edge.
    const observer = new IntersectionObserver(([entry]) =>
      setAway(!entry.isIntersecting && entry.boundingClientRect.bottom < window.innerHeight / 2),
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return away;
}
